import {
  ContentCopy as CloneIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Publish as PublishIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { Manufacturer, UserRole } from '../../types';
import { DateUtils } from '../../utils/date-utils';
import { useGetActiveManufacturersQuery } from '../manufacturer/manufacturers-api-slice';
import { selectCurrentUser } from '../user/user-slice';
import CreateVersionDialog, { CreateVersionData } from './components/create-version-dialog';
import {
  useCreateEmptyVersionMutation,
  useCreateVersionFromCopyMutation,
  useDeleteVersionMutation,
  useGetVersionsByManufacturerIdQuery,
} from './price-management-api-slice';
import { PriceVersion } from './types';
import PublishVersionModal from './publish-version-modal';

/**
 * Bundle Cost Management Dashboard — version listing with full CRUD actions
 */
const BundleCostManagementDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useAppSelector(selectCurrentUser);
  const isAdmin = currentUser?.role === UserRole.ADMINISTRATOR;
  const canManageVersions =
    currentUser?.role === UserRole.ADMINISTRATOR || currentUser?.role === UserRole.SUPERVISOR;

  // Manufacturer selection
  const { data: manufacturers = [] } = useGetActiveManufacturersQuery(undefined, {
    skip: !isAdmin,
  });
  const [selectedManufacturerId, setSelectedManufacturerId] = useState<number | undefined>(
    isAdmin ? undefined : currentUser?.manufacturerId
  );

  // Auto-select if only one manufacturer
  useEffect(() => {
    if (isAdmin && manufacturers.length === 1) {
      setSelectedManufacturerId(manufacturers[0].id);
    }
  }, [isAdmin, manufacturers]);

  // On mount: restore manufacturer selection and success toast passed via navigation state
  // (e.g. after publishing from the calculator page)
  useEffect(() => {
    const state = location.state as {
      publishedManufacturerId?: number;
      successMessage?: string;
    } | null;
    if (!state) return;

    if (state.publishedManufacturerId) {
      setSelectedManufacturerId(state.publishedManufacturerId);
    }
    if (state.successMessage) {
      setSuccessMessage(state.successMessage);
    }
    // Clear the state from history so it doesn't re-trigger on back/forward
    navigate(location.pathname, { replace: true, state: null });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch versions
  const {
    data: versions = [],
    isLoading: versionsLoading,
    error: versionsError,
  } = useGetVersionsByManufacturerIdQuery(selectedManufacturerId!, {
    skip: !selectedManufacturerId,
    refetchOnMountOrArgChange: true,
  });

  // Mutations
  const [createEmptyVersion, { isLoading: isCreatingEmpty }] = useCreateEmptyVersionMutation();
  const [createVersionFromCopy, { isLoading: isCreatingCopy }] = useCreateVersionFromCopyMutation();
  const [deleteVersion, { isLoading: isDeletingVersion }] = useDeleteVersionMutation();

  const isCreatingVersion = isCreatingEmpty || isCreatingCopy;

  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteConfirmVersion, setDeleteConfirmVersion] = useState<PriceVersion | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [publishVersion, setPublishVersion] = useState<PriceVersion | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Sort versions descending by version number
  const sortedVersions = useMemo(
    () => [...versions].sort((a, b) => b.versionNumber - a.versionNumber),
    [versions]
  );

  // Active published version (Published + no end date)
  const currentActiveVersion = useMemo(
    () => versions.find(v => v.status === 'Published' && !v.endDate),
    [versions]
  );

  const draftVersionsCount = useMemo(
    () => versions.filter(v => v.status === 'Draft').length,
    [versions]
  );

  // Utility formatters
  const fmtDate = (d?: string | null) => (d ? DateUtils.toDisplayDate(d) || '—' : '—');

  const truncate = (text?: string, max = 50) => {
    if (!text) return '—';
    return text.length > max ? text.slice(0, max) + '…' : text;
  };

  // Navigation handlers
  const handleViewVersion = useCallback(
    (id: number) => navigate(`/bundle-cost-calculator?versionId=${id}`),
    [navigate]
  );

  const handleEditVersion = useCallback(
    (id: number) => navigate(`/bundle-cost-calculator?versionId=${id}&mode=edit`),
    [navigate]
  );

  // Clone: copy the published version, navigate to new draft
  const handleCloneVersion = async (version: PriceVersion) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const newVersion = await createVersionFromCopy({
        sourceVersionId: version.id,
        changeDescription: `Cloned from Version ${version.versionNumber}.0`,
        effectiveDate: today,
      }).unwrap();
      navigate(`/bundle-cost-calculator?versionId=${newVersion.id}&mode=edit`);
    } catch {
      setErrorMessage('Failed to clone version. Please try again.');
    }
  };

  // Delete: show confirmation dialog then call API
  const handleDeleteConfirm = async () => {
    if (!deleteConfirmVersion) return;
    setDeleteError(null);
    try {
      await deleteVersion(deleteConfirmVersion.id).unwrap();
      setDeleteConfirmVersion(null);
    } catch {
      setDeleteError('Failed to delete the version. Please try again.');
    }
  };

  // Create new version
  const handleCreateVersionConfirm = async (data: CreateVersionData) => {
    if (!selectedManufacturerId) return;
    try {
      let newVersion: PriceVersion;
      if (data.type === 'empty') {
        newVersion = await createEmptyVersion({ manufacturerId: selectedManufacturerId }).unwrap();
      } else {
        if (!data.sourceVersionId) return;
        const today = new Date().toISOString().split('T')[0];
        const sourceVersion = versions.find(v => v.id === data.sourceVersionId);
        newVersion = await createVersionFromCopy({
          sourceVersionId: data.sourceVersionId,
          changeDescription: `New version created from Version ${sourceVersion?.versionNumber || ''}.0`,
          effectiveDate: today,
        }).unwrap();
      }
      setShowCreateDialog(false);
      navigate(`/bundle-cost-calculator?versionId=${newVersion.id}&mode=edit`, {
        state: {
          successMessage: `Version ${newVersion.versionNumber}.0 created successfully!`,
        },
      });
    } catch (error) {
      console.error('Error creating version:', error);
      throw error;
    }
  };

  const selectedManufacturer = manufacturers.find(m => m.id === selectedManufacturerId);
  const manufacturerDisplayName = isAdmin
    ? selectedManufacturer
      ? `${selectedManufacturer.licenseeName} (${selectedManufacturer.code})`
      : ''
    : currentUser?.manufacturer || '';

  // ─── Dealer access restricted ────────────────────────────────────────────────
  if (currentUser?.role === UserRole.DEALER) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Bundle Cost Management
        </Typography>
        <Alert severity="error" sx={{ mt: 1 }}>
          <strong>Access Restricted</strong> — Bundle Cost Management is not available for Dealer
          accounts. Please contact your manufacturer representative for pricing information.
        </Alert>
      </Box>
    );
  }

  // ─── No manufacturer selected ───────────────────────────────────────────────
  if (!selectedManufacturerId) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Bundle Cost Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage bundle costs and pricing versions for materials. Track historical pricing and
              publish new versions.
            </Typography>
          </Box>
        </Box>
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Select Manufacturer
          </Typography>
          <Autocomplete
            options={manufacturers}
            getOptionLabel={(m: Manufacturer) => `${m.licenseeName} (${m.code})`}
            value={manufacturers.find(m => m.id === selectedManufacturerId) ?? null}
            onChange={(_, m) => setSelectedManufacturerId(m?.id)}
            renderInput={params => (
              <TextField {...params} placeholder="Select a manufacturer..." size="small" />
            )}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            sx={{ maxWidth: 400 }}
          />
        </Paper>
        <Paper elevation={1} sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Select a Manufacturer to Get Started
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Choose a manufacturer from the dropdown above to view and manage their bundle cost
            versions.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // ─── Main page ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Bundle Cost Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage bundle costs and pricing versions for materials. Track historical pricing and
            publish new versions.
          </Typography>
        </Box>
        {canManageVersions && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowCreateDialog(true)}
            sx={{ ml: 2, mt: 0.5 }}
          >
            Create New Version
          </Button>
        )}
      </Box>

      {/* Manufacturer Selection — Admin only */}
      {isAdmin && (
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Manufacturer
          </Typography>
          <Autocomplete
            options={manufacturers}
            getOptionLabel={(m: Manufacturer) => `${m.licenseeName} (${m.code})`}
            value={manufacturers.find(m => m.id === selectedManufacturerId) ?? null}
            onChange={(_, m) => setSelectedManufacturerId(m?.id)}
            renderInput={params => (
              <TextField {...params} placeholder="Select a manufacturer..." size="small" />
            )}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            sx={{ maxWidth: 400 }}
          />
        </Paper>
      )}

      {/* Error */}
      {versionsError != null && (
        <Paper
          elevation={1}
          sx={{ p: 2, mb: 3, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}
        >
          <Typography color="error.main">Error loading versions. Please try again.</Typography>
        </Paper>
      )}

      {/* Loading */}
      {versionsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : versions.length === 0 ? (
        /* Empty State */
        <Paper elevation={1} sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            No Bundle Cost Versions Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {canManageVersions
              ? "This manufacturer doesn't have any bundle cost versions yet. Create your first version to get started."
              : 'There are no bundle cost versions for this manufacturer yet.'}
          </Typography>
          {canManageVersions && (
            <Button variant="contained" color="primary" onClick={() => setShowCreateDialog(true)}>
              Create First Version
            </Button>
          )}
        </Paper>
      ) : (
        <>
          {/* Summary Cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            {currentActiveVersion && (
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  flex: 1,
                  border: '1px solid',
                  borderColor: 'success.300',
                  bgcolor: 'success.50',
                }}
              >
                <Chip label="ACTIVE VERSION" size="small" color="success" sx={{ mb: 1 }} />
                <Typography variant="h5" gutterBottom>
                  Version {currentActiveVersion.versionNumber}.0
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Published: {fmtDate(currentActiveVersion.publishedDate)} by{' '}
                  {currentActiveVersion.publishedByName ||
                    currentActiveVersion.publishedBy ||
                    'N/A'}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={() => handleViewVersion(currentActiveVersion.id)}
                >
                  View Details
                </Button>
              </Paper>
            )}
            {draftVersionsCount > 0 && (
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  flex: 1,
                  border: '1px solid',
                  borderColor: 'warning.300',
                  bgcolor: 'warning.50',
                }}
              >
                <Chip
                  label={`${draftVersionsCount} DRAFT VERSION${draftVersionsCount > 1 ? 'S' : ''}`}
                  size="small"
                  sx={{ bgcolor: 'warning.main', color: 'white', mb: 1 }}
                />
                {(() => {
                  const draft = sortedVersions.find(v => v.status === 'Draft');
                  return draft ? (
                    <>
                      <Typography variant="h5" gutterBottom>
                        Version {draft.versionNumber}.0
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Created: {fmtDate(draft.created)} by{' '}
                        {draft.createdByName || draft.createdBy || 'N/A'}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mt: 2 }}
                        onClick={() =>
                          canManageVersions && (isAdmin || currentUser?.id === draft.createdBy)
                            ? handleEditVersion(draft.id)
                            : handleViewVersion(draft.id)
                        }
                      >
                        {canManageVersions && (isAdmin || currentUser?.id === draft.createdBy)
                          ? 'Continue Editing'
                          : 'View Details'}
                      </Button>
                    </>
                  ) : null;
                })()}
              </Paper>
            )}
          </Box>

          {/* Version History Table */}
          <Paper elevation={1} sx={{ overflow: 'hidden' }}>
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Typography variant="h6">Version History</Typography>
              <Typography variant="body2" color="text.secondary">
                Complete history of all pricing versions.
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: 'hidden' }}>
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '14%' }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    {[
                      'Version',
                      'Status',
                      'Effective Date',
                      'End Date',
                      'Published Date',
                      'Created By',
                      'Created Date',
                      'Change Summary',
                      'Actions',
                    ].map(label => (
                      <TableCell
                        key={label}
                        align={label === 'Actions' ? 'center' : 'left'}
                        sx={{ fontWeight: 600, bgcolor: 'grey.50', py: 1.25 }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedVersions.map((v, idx) => {
                    const isDraft = v.status === 'Draft';
                    const isPublished = v.status === 'Published';
                    const isActive = currentActiveVersion?.id === v.id;
                    const summary = truncate(v.changeDescription);
                    const hasFullSummary = v.changeDescription && v.changeDescription.length > 50;

                    return (
                      <TableRow
                        key={v.id}
                        hover
                        sx={{
                          bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        {/* Version Number */}
                        <TableCell sx={{ fontSize: '0.875rem' }}>
                          <Typography variant="body2" fontWeight={isPublished ? 700 : 400}>
                            Version {v.versionNumber}.0
                          </Typography>
                        </TableCell>

                        {/* Status */}
                        <TableCell>
                          <Chip
                            label={v.status}
                            size="small"
                            color={isPublished ? 'success' : 'default'}
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              bgcolor: isPublished ? 'success.main' : 'grey.300',
                              color: isPublished ? 'white' : 'grey.800',
                            }}
                          />
                        </TableCell>

                        {/* Effective Date */}
                        <TableCell
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: isActive ? 700 : 400,
                          }}
                        >
                          {fmtDate(v.effectiveDate)}
                        </TableCell>

                        {/* End Date */}
                        <TableCell sx={{ fontSize: '0.875rem' }}>
                          {isActive ? (
                            <Tooltip title="This version is currently active">
                              <Chip
                                label="Current"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            </Tooltip>
                          ) : (
                            fmtDate(v.endDate)
                          )}
                        </TableCell>

                        {/* Published Date */}
                        <TableCell
                          sx={{
                            fontSize: '0.875rem',
                            color: isDraft ? 'text.disabled' : 'text.primary',
                          }}
                        >
                          {isDraft ? '—' : fmtDate(v.publishedDate)}
                        </TableCell>

                        {/* Created By */}
                        <TableCell sx={{ fontSize: '0.875rem' }}>
                          {v.createdByName || v.createdBy || '—'}
                        </TableCell>

                        {/* Created Date */}
                        <TableCell sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                          {fmtDate(v.created)}
                        </TableCell>

                        {/* Change Summary */}
                        <TableCell sx={{ fontSize: '0.875rem' }}>
                          {hasFullSummary ? (
                            <Tooltip title={v.changeDescription!} placement="top">
                              <span style={{ cursor: 'default' }}>{summary}</span>
                            </Tooltip>
                          ) : (
                            summary
                          )}
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0, justifyContent: 'center' }}>
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewVersion(v.id)}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            {isDraft &&
                              canManageVersions &&
                              (isAdmin || currentUser?.id === v.createdBy) && (
                                <>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleEditVersion(v.id)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Publish">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => setPublishVersion(v)}
                                    >
                                      <PublishIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => {
                                        setDeleteError(null);
                                        setDeleteConfirmVersion(v);
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}

                            {isPublished && canManageVersions && (
                              <Tooltip title="Clone to new draft">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleCloneVersion(v)}
                                  disabled={isCreatingCopy}
                                >
                                  <CloneIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteConfirmVersion)}
        onClose={() => !isDeletingVersion && setDeleteConfirmVersion(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Version?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            <strong>Version {deleteConfirmVersion?.versionNumber}.0</strong>? This will permanently
            remove all bundle costs and hardware items in this draft. This action cannot be undone.
          </DialogContentText>
          {deleteError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {deleteError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmVersion(null)} disabled={isDeletingVersion}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={isDeletingVersion}
          >
            {isDeletingVersion ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Version Dialog */}
      {selectedManufacturerId && canManageVersions && (
        <CreateVersionDialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          onConfirm={handleCreateVersionConfirm}
          manufacturerName={manufacturerDisplayName}
          existingVersions={versions}
          isLoading={isCreatingVersion}
        />
      )}

      {/* Publish Version Modal */}
      {publishVersion && (
        <PublishVersionModal
          open={Boolean(publishVersion)}
          version={publishVersion}
          manufacturerName={manufacturerDisplayName}
          onClose={() => setPublishVersion(null)}
          onSuccess={published => {
            setPublishVersion(null);
            setSuccessMessage(`Version ${published.versionNumber}.0 published successfully!`);
          }}
        />
      )}

      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BundleCostManagementDashboardPage;
